import { Alert, Badge, Button, DataTable, dialogs, Drawer, EmptyState, Field, Input, Modal, PageHeader, Pagination, SearchSelect, SegmentedControl, Switch, Textarea, toast, useDebouncedValue, type Option } from "@cafe/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Megaphone, Send } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { api, ApiError, dt, errorText, http, useCan } from "../lib/api";

type Stats = { sent: number; pending: number; failed: number };
type Row = {
  id: string; title: string; body: string; target: "all" | "companies"; roles: string[]; channels: string[];
  companies: { id: string; name: string }[]; companiesCount: number; recipients: number; read: number;
  telegram: Stats; email: Stats; author: string | null; createdAt: string;
};
type Role = { code: string; name: string; system: boolean };
type Preview = { companies: number; recipients: number; telegram: number; email: number };

const plural = (n: number, one: string, few: string, many: string) => {
  const m10 = n % 10, m100 = n % 100;
  return `${n} ${m10 === 1 && m100 !== 11 ? one : m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14) ? few : many}`;
};
const people = (n: number) => plural(n, "сотрудник", "сотрудника", "сотрудников");
const companiesText = (n: number) => plural(n, "компания", "компании", "компаний");

const loadCompanies = async (q: string): Promise<Option[]> => {
  const r = await api<{ items: { id: string; name: string; planName: string | null }[] }>("GET", "/companies", undefined, { withMeta: true, query: { q: q || undefined, pageSize: 30 } });
  return r.items.map((c) => ({ value: c.id, label: c.name, description: c.planName ?? undefined }));
};

/** Новое сообщение: кому (все / выбранные компании), каким ролям, текст, каналы; сколько человек получат — сразу. */
function ComposeModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [target, setTarget] = useState<"all" | "companies">("companies");
  const [companies, setCompanies] = useState<Option[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [telegram, setTelegram] = useState(true);
  const [email, setEmail] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const companyIds = companies.map((c) => c.value);
  const single = target === "companies" && companyIds.length === 1 ? companyIds[0] : undefined;
  const roleList = useQuery({ queryKey: ["broadcast-roles", single], queryFn: () => http.get<Role[]>("/broadcasts/roles", { companyId: single }) });
  const ready = target === "all" || companyIds.length > 0;
  const audience = useDebouncedValue(JSON.stringify({ target, companyIds, roles }), 300);
  const preview = useQuery({
    queryKey: ["broadcast-preview", audience], enabled: ready,
    queryFn: () => http.post<Preview>("/broadcasts/preview", { ...(JSON.parse(audience) as object), title: "—", body: "—" }),
  });
  const p = ready ? preview.data : undefined;
  const payload = { target, companyIds: target === "all" ? [] : companyIds, roles, title, body, telegram, email };

  const send = () => {
    setErrors({});
    void dialogs.confirm({
      title: "Отправить сообщение?",
      description: `${p ? `Получат ${people(p.recipients)} в ${plural(p.companies, "компании", "компаниях", "компаниях")}. ` : ""}Отменить отправку будет нельзя.`,
      confirmText: "Отправить",
      onConfirm: async () => {
        try {
          const r = await http.post<{ companies: number; recipients: number }>("/broadcasts", payload);
          toast.success(`Отправлено: ${people(r.recipients)}`);
          void qc.invalidateQueries({ queryKey: ["broadcasts"] });
          onClose();
        } catch (e) {
          if (e instanceof ApiError && e.fields.length) setErrors(Object.fromEntries(e.fields.map((f) => [f.path, f.message])));
          else toast.error(errorText(e));
        }
      },
    });
  };

  return (
    <Modal open onOpenChange={(o) => !o && onClose()} size="lg" title="Новое сообщение клиентам"
      description="Придёт в колокольчик в кабинете клиента, а также в Telegram и на почту — если выбрать."
      footer={<>
        <Button variant="outline" onClick={onClose}>Отмена</Button>
        <Button leftIcon={<Send />} disabled={!ready || !title.trim() || !body.trim() || p?.recipients === 0} onClick={send}>Отправить</Button>
      </>}>
      <div className="flex flex-col gap-5">
        <Field label="Кому">
          <SegmentedControl value={target} onValueChange={(v) => setTarget(v as typeof target)}
            options={[{ value: "companies", label: "Выбранным компаниям" }, { value: "all", label: "Всем клиентам" }]} />
        </Field>
        {target === "companies" && (
          <Field label="Компании" required error={errors.companyIds} hint="Одна или несколько — начните вводить название, ИНН или email">
            <SearchSelect multiple value={companyIds} selectedOptions={companies} loadOptions={loadCompanies} placeholder="Выберите компании"
              onValueChange={(_, opts) => setCompanies(opts)} />
          </Field>
        )}
        {target === "all" && <Alert tone="info">Всем клиентам, кроме архивных — и тем, у кого подписка приостановлена.</Alert>}
        <Field label="Роли" error={errors.roles} hint={single ? "Пусто — всем сотрудникам. Свои роли этой компании тоже в списке." : "Пусто — всем сотрудникам. Например, только владельцам."}>
          <SearchSelect multiple value={roles} onValueChange={setRoles} placeholder="Все сотрудники"
            options={(roleList.data ?? []).map((r) => ({ value: r.code, label: r.name, description: r.system ? undefined : "своя роль компании" }))} />
        </Field>
        <Field label="Заголовок" required error={errors.title}><Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="Например: Обновление кассы 12 ноября" /></Field>
        <Field label="Текст" required error={errors.body} hint={`${body.length} / 3000`}>
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} maxLength={3000} placeholder="Простыми словами: что случилось и что нужно сделать" />
        </Field>
        <div className="flex flex-col gap-3">
          <Switch checked={telegram} onCheckedChange={setTelegram} label="Отправить в Telegram" description="Тем, у кого привязан бот уведомлений" />
          <Switch checked={email} onCheckedChange={setEmail} label="Отправить на почту" description="Тем, у кого указан email" />
        </div>
        {p && (
          <Alert tone={p.recipients ? "success" : "warning"}>
            {p.recipients
              ? `Получат ${people(p.recipients)} в ${plural(p.companies, "компании", "компаниях", "компаниях")}${telegram ? ` · Telegram привязан у ${p.telegram}` : ""}${email ? ` · email есть у ${p.email}` : ""}`
              : "Нет получателей: в выбранных компаниях нет сотрудников с такими ролями"}
          </Alert>
        )}
      </div>
    </Modal>
  );
}

function Delivery({ s }: { s: Stats }) {
  return (
    <span className="tabular-nums">
      {s.sent}{s.pending > 0 && <span className="text-muted"> · ждёт {s.pending}</span>}{s.failed > 0 && <span className="text-danger"> · не дошло {s.failed}</span>}
    </span>
  );
}

function Details({ r, roleNames, onClose }: { r: Row; roleNames: Map<string, string>; onClose: () => void }) {
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()} width="md" title={r.title} description={`${dt(r.createdAt)}${r.author ? ` · ${r.author}` : ""}`}>
      <div className="flex flex-col gap-4 text-sm">
        <p className="rounded-tile bg-surface-2 px-4 py-3 whitespace-pre-line">{r.body}</p>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
          <dt className="text-muted">Кому</dt>
          <dd>{r.target === "all" ? "Всем клиентам" : (
            <span className="flex flex-wrap gap-x-2">{r.companies.map((c) => <Link key={c.id} to={`/companies/${c.id}`} className="text-primary hover:underline">{c.name}</Link>)}</span>
          )}</dd>
          <dt className="text-muted">Роли</dt>
          <dd>{r.roles.length ? r.roles.map((c) => roleNames.get(c) ?? c).join(", ") : "Все сотрудники"}</dd>
          <dt className="text-muted">Получили</dt>
          <dd>{people(r.recipients)} в {plural(r.companiesCount, "компании", "компаниях", "компаниях")}</dd>
          <dt className="text-muted">Прочитали</dt><dd className="tabular-nums">{r.read}</dd>
          {r.channels.includes("telegram") && <><dt className="text-muted">Telegram</dt><dd><Delivery s={r.telegram} /></dd></>}
          {r.channels.includes("email") && <><dt className="text-muted">Почта</dt><dd><Delivery s={r.email} /></dd></>}
        </dl>
      </div>
    </Drawer>
  );
}

/** Рассылки: сообщения от Coca одной, нескольким или всем компаниям — в кабинет, Telegram и на почту. */
export default function BroadcastsPage() {
  const can = useCan();
  const [page, setPage] = useState(1);
  const [composing, setComposing] = useState(false);
  const [open, setOpen] = useState<Row | null>(null);
  const list = useQuery({
    queryKey: ["broadcasts", page],
    queryFn: () => api<{ items: Row[]; meta: { total: number } }>("GET", "/broadcasts", undefined, { withMeta: true, query: { page, pageSize: 30 } }),
  });
  const roles = useQuery({ queryKey: ["broadcast-roles", undefined], queryFn: () => http.get<Role[]>("/broadcasts/roles"), enabled: can("broadcasts.send") });
  const roleNames = new Map((roles.data ?? []).map((r) => [r.code, r.name]));
  return (
    <>
      <PageHeader title="Рассылки" description="Сообщения клиентам: в колокольчик кабинета, в Telegram и на почту."
        actions={can("broadcasts.send") && <Button leftIcon={<Send />} onClick={() => setComposing(true)}>Новое сообщение</Button>} />
      {list.isError && <Alert tone="danger">{errorText(list.error)}</Alert>}
      <DataTable rows={list.data?.items} loading={list.isPending} rowKey={(r) => r.id} onRowClick={setOpen}
        empty={<EmptyState icon={<Megaphone />} title="Сообщений ещё не было" description="Например: плановые работы, новая функция, напоминание об оплате." />}
        columns={[
          { key: "date", header: "Когда", cell: (r) => dt(r.createdAt), mobile: "subtitle" },
          { key: "title", header: "Сообщение", cell: (r) => <span className="font-medium">{r.title}</span>, mobile: "title" },
          {
            key: "to", header: "Кому", cell: (r) => (
              <span className="flex flex-wrap items-center gap-1">
                {r.target === "all" ? <Badge tone="primary">Всем</Badge> : r.companies.length === 1 ? r.companies[0]!.name : companiesText(r.companies.length)}
                {r.roles.length > 0 && <span className="text-xs text-muted">· {r.roles.map((c) => roleNames.get(c) ?? c).join(", ")}</span>}
              </span>
            ),
          },
          { key: "recipients", header: "Получили / прочитали", align: "end", cell: (r) => <span className="tabular-nums">{r.recipients} / {r.read}</span> },
          { key: "tg", header: "Telegram", align: "end", cell: (r) => (r.channels.includes("telegram") ? <Delivery s={r.telegram} /> : "—") },
          { key: "author", header: "Отправил", cell: (r) => r.author ?? "—" },
        ]} />
      {list.data && <Pagination page={page} pageSize={30} total={list.data.meta.total} onPageChange={setPage} />}
      {composing && <ComposeModal onClose={() => setComposing(false)} />}
      {open && <Details r={open} roleNames={roleNames} onClose={() => setOpen(null)} />}
    </>
  );
}
